'use client';

import Link from 'next/link';
import { Heart, Loader2 } from 'lucide-react';
import { useBasePath } from '../navigation/base-path';
import { S3Image } from './s3-image';
import { VideoThumbnail } from './video-thumbnail';
import { cn } from '../utils/cn';

const tabs = [
  { value: 'all', label: 'Все' },
  { value: 'new', label: 'Новые' },
  { value: 'liked', label: 'Мои лайки' },
] as const;

export type FeedScreenTabValue = (typeof tabs)[number]['value'];

export interface FeedScreenTag {
  id: string;
  name: string;
}

export interface FeedScreenItem {
  id: string;
  resultUrl: string | null;
  thumbnailUrl: string | null;
  resultType: 'IMAGE' | 'VIDEO' | 'AUDIO';
  prompt: string | null;
  likesCount: number;
  createdAt: string;
}

interface FeedScreenProps {
  activeTab: FeedScreenTabValue;
  activeTag: string | null;
  feedTags?: FeedScreenTag[];
  feedItems?: FeedScreenItem[];
  isLoading?: boolean;
  isFetching?: boolean;
  onTabChange: (tab: FeedScreenTabValue) => void;
  onTagToggle: (tagId: string) => void;
  onLike: (id: string) => void;
}

export function FeedScreen({
  activeTab,
  activeTag,
  feedTags = [],
  feedItems,
  isLoading = false,
  isFetching = false,
  onTabChange,
  onTagToggle,
  onLike,
}: FeedScreenProps) {
  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => onTabChange(tab.value)}
            className={cn(
              'rounded-full px-4 py-2 text-sm font-medium transition-colors',
              activeTab === tab.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary hover:bg-secondary/80',
            )}
          >
            {tab.label}
          </button>
        ))}
        {isFetching && (
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-muted-foreground" />
        )}
      </div>

      {feedTags.length > 0 && (
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {feedTags.map((tag) => (
            <button
              key={tag.id}
              onClick={() => onTagToggle(tag.id)}
              className={cn(
                'flex-shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-colors',
                activeTag === tag.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary hover:bg-secondary/80',
              )}
            >
              {tag.name}
            </button>
          ))}
        </div>
      )}

      {isLoading && !feedItems ? (
        <div className="grid grid-cols-2 gap-2">
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <div
              key={item}
              className="aspect-square animate-pulse rounded-xl bg-secondary"
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {feedItems?.map((item) => (
            <FeedCard key={item.id} item={item} onLike={onLike} />
          ))}
        </div>
      )}

      {feedItems?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-muted-foreground">Здесь пока ничего нет</p>
        </div>
      )}
    </div>
  );
}

function FeedCard({
  item,
  onLike,
}: {
  item: FeedScreenItem;
  onLike: (id: string) => void;
}) {
  const basePath = useBasePath();
  return (
    <Link href={`${basePath}/feed/${item.id}`} className="group relative">
      <div className="relative aspect-square overflow-hidden rounded-xl bg-secondary">
        {item.resultUrl && (
          item.resultType === 'VIDEO' ? (
            <VideoThumbnail
              src={item.resultUrl}
              poster={item.thumbnailUrl}
              alt={item.prompt || 'Video'}
              iconSize="lg"
            />
          ) : (
            <S3Image
              src={item.thumbnailUrl || item.resultUrl}
              alt={item.prompt || 'Generated'}
              fill
              className="object-cover group-hover:scale-105"
            />
          )
        )}

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/80">
              {formatRelativeTime(item.createdAt)}
            </span>
            <button
              onClick={(event) => {
                event.preventDefault();
                onLike(item.id);
              }}
              className="flex items-center gap-1 rounded-full bg-black/30 px-2 py-1"
            >
              <Heart
                className={cn('h-3 w-3', item.likesCount > 0 && 'fill-red-500 text-red-500')}
              />
              <span className="text-xs text-white">{item.likesCount}</span>
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}

function formatRelativeTime(date: string | Date): string {
  const value = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = now.getTime() - value.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'только что';
  if (minutes < 60) return `${minutes} мин. назад`;
  if (hours < 24) return `${hours} ч. назад`;
  if (days < 7) return `${days} дн. назад`;

  return value.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
